import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Search, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { api, getErrorMessage } from "@/api";

const formatEventDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const [year, month, day] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(
    new Date(year, month - 1, day),
  );
};

export default function JobsPage({ user }) {
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [letter, setLetter] = useState("");

  const loadJobs = async () => {
    const { data } = await api.get("/jobs", { params: { search } });
    setJobs(data);
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const apply = async (event) => {
    event.preventDefault();
    try {
      await api.post(`/jobs/${selectedJob.id}/applications`, { cover_letter: letter });
      toast.success("Your application is on its way.");
      setLetter("");
      setSelectedJob(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <main className="jobs-page" data-testid="jobs-page">
      <section className="jobs-hero" data-testid="jobs-hero">
        <div className="jobs-hero-overlay" />
        <div className="jobs-hero-copy">
          <p className="eyebrow"><Sparkles size={15} /> THE LIVE WORK EDIT</p>
          <h1>Work worth<br /><em>showing up for.</em></h1>
          <p>Exceptional briefs from teams shaping what people will remember.</p>
        </div>
      </section>
      <section className="jobs-content">
        <div className="section-heading" data-testid="jobs-section-heading">
          <div>
            <p className="eyebrow">OPEN CALLS</p>
            <h2>Find your next room.</h2>
          </div>
          <div className="jobs-search">
            <Search size={18} />
            <input
              data-testid="jobs-search-input"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search role, event, location"
              value={search}
            />
            <button data-testid="jobs-search-button" onClick={loadJobs} type="button">Search</button>
          </div>
        </div>
        <div className="job-list" data-testid="job-list">
          {jobs.map((job) => (
            <article className="job-row" data-testid={`job-card-${job.id}`} key={job.id}>
              <div className="job-date" data-testid={`job-date-${job.id}`}>{formatEventDate(job.event_date)}</div>
              <div className="job-detail">
                <span className="job-category" data-testid={`job-category-${job.id}`}>{job.category}</span>
                <h3 data-testid={`job-title-${job.id}`}>{job.title}</h3>
                <p data-testid={`job-event-${job.id}`}>{job.event_name}</p>
                <div className="job-meta" data-testid={`job-meta-${job.id}`}>
                  <span><MapPin size={15} />{job.location}</span>
                  <span><CalendarDays size={15} />${job.budget.toLocaleString()} project</span>
                </div>
              </div>
              <button
                className="outline-button"
                data-testid={`job-apply-button-${job.id}`}
                onClick={() => setSelectedJob(job)}
                type="button"
              >
                {user?.role === "freelancer" ? "Apply" : "View brief"} <Send size={15} />
              </button>
            </article>
          ))}
        </div>
      </section>
      {selectedJob && (
        <div className="modal-backdrop" data-testid="application-modal">
          <form className="application-modal" data-testid="application-form" onSubmit={apply}>
            <button className="modal-close" data-testid="application-close-button" onClick={() => setSelectedJob(null)} type="button">×</button>
            <p className="eyebrow">APPLICATION</p>
            <h2 data-testid="application-job-title">{selectedJob.title}</h2>
            {user?.role === "freelancer" ? (
              <>
                <textarea
                  data-testid="application-letter-input"
                  onChange={(event) => setLetter(event.target.value)}
                  placeholder="Introduce yourself and explain why you're a fit…"
                  required
                  value={letter}
                />
                <button className="primary-button" data-testid="application-submit-button">Send application</button>
              </>
            ) : (
              <Link className="primary-button" data-testid="application-join-link" to="/join/freelancer">
                Create a freelancer account
              </Link>
            )}
          </form>
        </div>
      )}
    </main>
  );
}